import { useEffect, useState } from 'react';
import { Turnstile, turnstileActivo } from '@/components/auth/Turnstile';
import { enviarRecuperacion, MENSAJE_NEUTRO, DURACION_ENLACE } from '@/lib/recuperarPassword';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Eye, EyeOff, Mail, Lock, ArrowRight, ShieldCheck,
  Scissors, CalendarCheck, Hotel, BarChart3, Bell,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { GroomingProLogo } from '@/components/layout/GroomingProLogo';
import { TelonMascotas, Huella } from '@/components/layout/FigurasMascotas';

/**
 * Pantalla de entrada.
 *
 * Aquí NO se crean cuentas. En un sistema multiempresa cada peluquería la da
 * de alta el superadministrador, que es quien decide a qué empresa pertenece
 * el usuario y con qué permisos entra. Cuando esta pantalla ofrecía "crea tu
 * peluquería", cualquiera con el enlace podía fabricarse una empresa dentro
 * del sistema.
 */

/**
 * El degradado sale del logo, que va de #10b981 (emerald-500) a #2dd4bf
 * (teal-400). Ese verde-a-turquesa es la identidad del sistema y es el mismo
 * par que usan el sidebar y los acentos; un verde bosque oscuro se vería de
 * otro producto.
 */
const HERO_GRADIENT =
  'radial-gradient(1100px 620px at 12% 8%, rgba(45,212,191,0.34), transparent 60%),' +
  'radial-gradient(920px 540px at 88% 92%, rgba(16,185,129,0.42), transparent 58%),' +
  'radial-gradient(680px 680px at 95% 4%, rgba(94,234,212,0.20), transparent 60%),' +
  'linear-gradient(145deg, #064e45 0%, #0b7a63 48%, #10b981 100%)';

const FEATURES = [
  { icon: CalendarCheck, title: 'Agenda y citas', desc: 'Cupos del día, adicionales y cobro en la misma cita' },
  { icon: Scissors, title: 'Servicios por tamaño', desc: 'Precio distinto para pequeño, mediano, grande y grande pro' },
  { icon: Hotel, title: 'Hotel y daycare', desc: 'Huéspedes, tarifas por noche y abonos con saldo' },
  { icon: Bell, title: 'Recordatorios', desc: 'Purga, garrapatas y vacuna al día, con WhatsApp listo' },
  { icon: BarChart3, title: 'Inventario y caja', desc: 'Compras, gastos, pagos divididos y cierre cuadrado' },
];

export function LoginPage() {
  const nav = useNavigate();
  // Campos vacíos: precargar credenciales deja la cuenta al alcance de
  // cualquiera que abra la pantalla, y en un sistema multiempresa eso es una
  // puerta abierta al negocio de otro.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ── Captcha ─────────────────────────────────────────────────────────
  const [captcha, setCaptcha] = useState<string | null>(null);
  // Hacen falta DOS banderas, no una: `!captcha` significa a la vez «todavía
  // estoy resolviendo» y «no voy a resolver jamás». Con una sola, un reto que
  // se cuelga deja el botón muerto para siempre y nadie entra. Ver
  // «Patrón — La espera que no termina nunca».
  const [captchaTermino, setCaptchaTermino] = useState(false);
  // El token se gasta al usarse: tras un intento fallido hay que reiniciar el
  // widget o el siguiente lo rechaza por repetido, y el usuario ve
  // «contraseña incorrecta» teniéndola bien.
  const [reiniciarCaptcha, setReiniciarCaptcha] = useState(0);

  // ── Recuperar la contraseña ─────────────────────────────────────────
  // Se contesta SIEMPRE lo mismo, exista o no el correo: decir «ese correo no
  // está registrado» convertiría esto en una herramienta para averiguar qué
  // direcciones tienen cuenta. Ver src/lib/recuperarPassword.ts.
  const [recuperando, setRecuperando] = useState(false);
  const [correoRecuperar, setCorreoRecuperar] = useState('');
  const [enviandoRecuperacion, setEnviandoRecuperacion] = useState(false);
  const [recuperacionEnviada, setRecuperacionEnviada] = useState(false);
  // Captcha propio del diálogo: `/recover` también lo exige, y el token del
  // formulario de entrada se gasta al usarse.
  const [captchaRecuperar, setCaptchaRecuperar] = useState<string | null>(null);
  const [reiniciarCaptchaRec, setReiniciarCaptchaRec] = useState(0);

  const abrirRecuperacion = () => {
    // Si ya escribió el correo arriba, se aprovecha: un campo menos.
    setCorreoRecuperar(email);
    setRecuperacionEnviada(false);
    setRecuperando(true);
  };

  const pedirRecuperacion = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviandoRecuperacion(true);
    try {
      // Token propio: el del formulario de entrada se gasta al usarse, y
      // usarlo aquí dejaría al usuario sin poder iniciar sesión después.
      const r = await enviarRecuperacion(correoRecuperar, false, captchaRecuperar);
      // Falla por formato inválido o por un fallo real del sistema; que el
      // correo no exista se calla a propósito.
      if (!r.ok && r.motivo) {
        setReiniciarCaptchaRec((n) => n + 1);
        setCaptchaRecuperar(null);
        toast.error(r.motivo);
        return;
      }
      setRecuperacionEnviada(true);
    } finally {
      setEnviandoRecuperacion(false);
    }
  };
  const [loading, setLoading] = useState(false);

  // Listen for tenant-access-blocked events fired from the auth store after
  // Supabase signs us in but the tenant is suspended/expired.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string; reason?: string }>).detail;
      toast.error(detail?.message ?? 'Acceso bloqueado', { duration: 8000 });
    };
    window.addEventListener('groomingpro:tenant-access-blocked', handler);
    return () => window.removeEventListener('groomingpro:tenant-access-blocked', handler);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // El token NO se comprueba aquí: viaja con las credenciales y lo valida
    // GoTrue contra Cloudflare, desde el servidor. Un captcha verificado en el
    // navegador no prueba nada.
    //
    // Se manda solo si existe: `captchaToken: null` hace que GoTrue lo trate
    // como un token vacío y lo rechace.
    const { error, data } = await supabase.auth.signInWithPassword(
      captcha ? { email, password, options: { captchaToken: captcha } } : { email, password },
    );
    setLoading(false);
    if (error) {
      // El token ya se gastó: el siguiente intento necesita uno nuevo.
      setReiniciarCaptcha((n) => n + 1);
      setCaptcha(null);
      setCaptchaTermino(false);

      // Un fallo del captcha no es una contraseña mal escrita. Decir lo mismo
      // en los dos casos manda al usuario a cambiar algo que está bien.
      if (/captcha|turnstile/i.test(error.message)) {
        toast.error('No se pudo verificar que eres una persona. Recarga la página e intenta de nuevo.');
        return;
      }
      // El mensaje de Supabase llega en inglés y filtra si el correo existe.
      toast.error('Correo o contraseña incorrectos. Verifica e intenta de nuevo.');
      return;
    }
    const name = data.user?.user_metadata?.first_name as string | undefined;
    toast.success(name ? `Bienvenida, ${name}` : 'Bienvenida');
    // Wait a tick for setSessionData to either confirm access or block;
    // if blocked it will toast and clear the session, so this nav becomes a no-op.
    setTimeout(() => nav('/dashboard'), 200);
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground lg:grid lg:grid-cols-[1.05fr_1fr]">
      <style>{`
        @keyframes ge-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        /* Las figuras flotan conservando su giro: la rotación va en una
           variable porque translate y rotate en la misma transform se pisan. */
        @keyframes ge-float {
          0%, 100% { transform: translateY(0) }
          50%      { transform: translateY(-14px) }
        }
        .ge-reveal { opacity: 0; animation: ge-fade-up .7s cubic-bezier(.22,.61,.36,1) forwards; }
        .ge-float  { animation: ge-float 8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .ge-reveal, .ge-float { animation: none !important; opacity: 1 !important; }
        }
      `}</style>

      {/* ══════════ Presentación (izquierda) ══════════ */}
      {/* Oculta en móvil: en un celular lo único que se quiere es entrar. */}
      <aside
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex xl:p-16"
        style={{ background: HERO_GRADIENT }}
      >
        {/* Trama de puntos + las figuras del oficio: sin ellas el panel queda
            plano y podría ser el de cualquier sistema. */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.10]"
             style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <TelonMascotas />

        <div className="relative ge-reveal">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md">
              <GroomingProLogo className="h-7 w-7" />
            </div>
            <div>
              <p className="font-display text-xl font-bold tracking-tight">Grooming ERP</p>
              <p className="text-xs text-white/60">Peluquería, hotel y daycare</p>
            </div>
          </div>

          <h2 className="mt-12 flex max-w-lg items-start gap-3 font-display text-4xl font-bold leading-tight xl:text-5xl">
            Todo el salón, en una sola pantalla.
            <Huella className="mt-1 hidden h-8 w-8 shrink-0 text-teal-200/70 xl:block" />
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/70">
            Desde que entra la mascota hasta que se cierra la caja.
          </p>
        </div>

        <ul className="relative mt-12 space-y-3">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <li
              key={title}
              className="ge-reveal flex items-start gap-3.5 rounded-xl border border-white/10 bg-white/[0.06] p-3.5 backdrop-blur-sm"
              style={{ animationDelay: `${120 + i * 90}ms` }}
            >
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-300/20 text-teal-100">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{title}</p>
                <p className="text-xs leading-relaxed text-white/60">{desc}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="relative mt-12 text-xs text-white/50">
          © {new Date().getFullYear()} Grooming ERP · Hecho con{' '}
          <span className="text-rose-300">♥</span> en Colombia
        </p>
      </aside>

      {/* ══════════ Formulario (derecha) ══════════ */}
      <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10 sm:px-8">
        <div className="w-full max-w-md">
          {/* En móvil la marca no aparece a la izquierda, así que va aquí. */}
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <GroomingProLogo className="h-12 w-12" />
            <h1 className="mt-3 font-display text-2xl font-bold">Grooming ERP</h1>
            <p className="mt-1 text-sm text-muted-foreground">Peluquería, hotel y daycare</p>
          </div>

          <div className="hidden lg:block">
            <h1 className="font-display text-3xl font-bold tracking-tight">Bienvenida de nuevo</h1>
            <p className="mt-2 text-[15px] text-muted-foreground">Ingresa con la cuenta de tu salón.</p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground">Correo</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="min-h-[48px] w-full rounded-xl border border-input bg-background pl-11 pr-4 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-foreground">Contraseña</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="min-h-[48px] w-full rounded-xl border border-input bg-background pl-11 pr-12 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                />
                {/* Escribir una contraseña a ciegas en un celular es la primera
                    causa de "no me deja entrar". */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={abrirRecuperacion}
                className="min-h-[40px] text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {/* Si Cloudflare no responde se entra igual: dejar a la
                peluquería fuera de su propio sistema por un captcha caído es
                peor que el riesgo que el captcha evita. */}
            <Turnstile
              onToken={(t) => { setCaptcha(t); setCaptchaTermino(true); }}
              reiniciar={reiniciarCaptcha}
            />

            <button
              type="submit"
              disabled={loading || (turnstileActivo && !captcha && !captchaTermino)}
              className="group flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 text-[15px] font-semibold text-primary-foreground shadow-soft transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Ingresando…
                </>
              ) : (
                <>
                  Ingresar
                  <ArrowRight className="h-[18px] w-[18px] transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          {/* ── Pie legal ──────────────────────────────────────────────
              Las cuentas las crea el superadministrador, así que aquí no hay
              "crear cuenta": solo a quién escribirle si no puede entrar. */}
          <div className="mt-8 border-t border-border pt-6 text-center">
            <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              Conexión segura con encriptación de datos
            </p>
            <p className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <a
                href="/politica-tratamiento-datos"
                className="rounded underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                Política de tratamiento de datos
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="/terminos-y-condiciones"
                className="rounded underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                Términos y condiciones
              </a>
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Diseñado y desarrollado por{' '}
              {/* Abre en otra pestaña: quien está entrando a su sistema no
                  debería perder el login por leer quién lo hizo.
                  `noopener` porque `target="_blank"` deja a la página abierta
                  acceso a esta por `window.opener`. */}
              <a
                href="https://netcloudnetworking.com"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded font-medium underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                Netcloud Networking
              </a>{' '}
              · © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </main>

      {/* ══════════ Recuperar contraseña ══════════
          Va como capa sobre el login y no como pantalla aparte: quien llega
          aquí quería entrar, y sacarlo del login para pedir un correo lo hace
          sentir más lejos de conseguirlo. */}
      {recuperando && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 px-5 py-10 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-recuperar"
        >
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-7 shadow-lg">
            {recuperacionEnviada ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <h3 id="titulo-recuperar" className="mb-2 font-display font-semibold">
                  Revisa tu correo
                </h3>
                {/* El mismo texto exista o no la cuenta: ver MENSAJE_NEUTRO. */}
                <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                  {MENSAJE_NEUTRO}
                </p>
                <p className="mb-5 text-xs text-muted-foreground">{DURACION_ENLACE}</p>
                <button
                  onClick={() => setRecuperando(false)}
                  className="min-h-[44px] w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:opacity-90"
                >
                  Entendido
                </button>
              </div>
            ) : (
              <form onSubmit={pedirRecuperacion}>
                <h3 id="titulo-recuperar" className="mb-1.5 font-display font-semibold">
                  ¿Olvidaste tu contraseña?
                </h3>
                <p className="mb-5 text-sm text-muted-foreground">
                  Escribe tu correo y te enviamos un enlace para crear una nueva.
                </p>

                <div className="relative mb-4">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={correoRecuperar}
                    onChange={(e) => setCorreoRecuperar(e.target.value)}
                    placeholder="tucorreo@ejemplo.com"
                    autoFocus
                    required
                    className="min-h-[48px] w-full rounded-xl border border-input bg-background pl-11 pr-4 text-[15px] focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                  />
                </div>

                <Turnstile
                  onToken={setCaptchaRecuperar}
                  reiniciar={reiniciarCaptchaRec}
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setRecuperando(false)}
                    className="min-h-[44px] flex-1 rounded-xl border border-input text-sm font-medium hover:bg-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={enviandoRecuperacion}
                    className="min-h-[44px] flex-1 rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
                  >
                    {enviandoRecuperacion ? 'Enviando…' : 'Enviar enlace'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
